import { type CONVEYANCE_TYPES } from '@koala/sdk';
import { DATE_FORMAT } from '@/constants/dates';
import { getFormattedDateInTimezone } from '@/utils/dates';

export const COMMIT_CONFIRMATION_BODY = 'Menu availability has been refreshed.';

export interface CommitConfirmation {
  message: string;
  secondaryMessage: string;
}

/**
 * Copy for the toast confirming that a new handoff time was applied and the menu
 * refreshed (TEAM-14387).
 *
 * Returns `null` when there is nothing specific to confirm — an ASAP commit carries
 * no time at all (`ASAP_VALUE` is `''`), and AC5 says not to claim a scheduled clock
 * time in that case. `getFormattedDateInTimezone` already returns `''` for anything
 * unparseable, which covers `'asap'` and a malformed value too, so a null here is
 * the single "do not toast" signal for every caller.
 *
 * The weekday is always used, matching the comps ("Pickup time updated to Tue at
 * 11:00 PM") — deliberately not the "Today, …" special case that the store header
 * and `getTimeDisplay` apply, which the ACs do not ask for.
 */
export function buildCommitConfirmation(
  wantedAt: string | undefined,
  handoffType: CONVEYANCE_TYPES | string,
  storeTimezone: string,
): CommitConfirmation | null {
  const formatted = getFormattedDateInTimezone(
    wantedAt ?? '',
    DATE_FORMAT.WEEKDAY_AT_HOURS_MINUTES,
    storeTimezone,
  );

  if (!formatted) {
    return null;
  }

  return {
    message: `${handoffType} time updated to ${formatted}`,
    secondaryMessage: COMMIT_CONFIRMATION_BODY,
  };
}
